import {
  AuthorizationError,
  BadGatewayError,
  BadRequestError,
  NetworkError,
  NotFoundError,
  OfflineError,
  PaymentRequiredError,
  RateLimitExceededError,
  RequestError,
  ServiceUnavailableError,
  UnprocessableEntityError,
  UpdateRequiredError,
} from '@ametista-ai/api-schema/errors';
import { JSONObject } from '@ametista-ai/api-schema/json/types';
import retry from 'fetch-retry';
import queryString from 'query-string';
import { notifications } from '@mantine/notifications';
import i18n from '@/i18n';
import { Logger } from '@/logger';
import { getLocalStorageValue } from '@/utils/localStorage';

// Errors thrown by ApiClient for 4xx responses are deterministic: retrying
// the exact same request will fail the exact same way, so react-query
// shouldn't burn retries (and time) on them. Only genuinely transient
// errors (network blips, 502/503, rate limiting) are worth retrying.
const NON_RETRYABLE_ERRORS = [
  AuthorizationError,
  BadRequestError,
  NotFoundError,
  PaymentRequiredError,
  UnprocessableEntityError,
  UpdateRequiredError,
];

export const isRetryableApiError = (error: unknown) =>
  !NON_RETRYABLE_ERRORS.some((ErrorClass) => error instanceof ErrorClass);

// When present, authenticates every request via `Authorization: Bearer`
// instead of the session cookie — same application-wide API token the
// backend already accepts (takes priority over the cookie in
// resolveAuthUser()). Lets tools like the production canary suite drive
// the app without going through interactive login.
export const API_TOKEN_STORAGE_KEY = 'api-token';

type Options = {
  baseUrl?: string;
};

interface FetchOptions {
  download?: boolean;
  retry?: boolean;
  credentials?: 'omit' | 'same-origin' | 'include';
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

const fetchWithRetry = retry(fetch);

class ApiClient {
  baseUrl: string;

  shareId?: string;

  constructor(options: Options = {}) {
    this.baseUrl = options.baseUrl || '/api';
  }

  setShareId = (shareId: string | undefined) => {
    this.shareId = shareId;
  };

  fetch = async <T = unknown>(
    path: string,
    method: string,
    data: JSONObject | FormData | undefined,
    options: FetchOptions = {}
  ): Promise<T> => {
    let body: string | FormData | undefined;
    let modifiedPath;
    let urlToFetch;
    let isJson;

    if (this.shareId) {
      // add to data
      data = {
        ...(data || {}),
        shareId: this.shareId,
      };
    }

    if (method === 'GET') {
      if (data) {
        modifiedPath = `${path}?${data && queryString.stringify(data)}`;
      } else {
        modifiedPath = path;
      }
    } else if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
      if (data instanceof FormData || typeof data === 'string') {
        body = data;
      }

      // Only stringify data if its a normal object and
      // not if it's [object FormData], in addition to
      // toggling Content-Type to application/json
      if (typeof data === 'object' && (data || '').toString() === '[object Object]') {
        isJson = true;
        body = JSON.stringify(data);
      }
    }

    if (path.match(/^http/)) {
      urlToFetch = modifiedPath || path;
    } else {
      urlToFetch = this.baseUrl + (modifiedPath || path);
    }

    const apiToken = getLocalStorageValue(API_TOKEN_STORAGE_KEY);

    const headerOptions: Record<string, string> = {
      Accept: 'application/json',
      Origin: window.location.origin,
      'cache-control': 'no-cache',
      // 'x-editor-version': EDITOR_VERSION,
      // 'x-api-version': '3',
      pragma: 'no-cache',
      ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}),
      ...options?.headers,
    };

    // for multipart forms or other non JSON requests fetch
    // populates the Content-Type without needing to explicitly
    // set it.
    if (isJson) {
      headerOptions['Content-Type'] = 'application/json';
    }

    const headers = new Headers(headerOptions);
    const timeStart = window.performance.now();
    let response;

    try {
      response = await (options?.retry === false ? fetch : fetchWithRetry)(urlToFetch, {
        method,
        body,
        headers,
        redirect: 'follow',
        credentials: 'same-origin',
        cache: 'no-cache',
        signal: options?.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      if (window.navigator.onLine) {
        notifications.show({
          title: i18n.t('error.network-error-title', 'Network Error'),
          message: i18n.t('error.network-error-message', 'A network error occurred, try again?'),
          color: 'red',
        });
        throw new NetworkError('A network error occurred, try again?');
      } else {
        notifications.show({
          title: i18n.t('error.offline-error-title', 'Offline'),
          message: i18n.t('error.offline-error-message', 'No internet connection available'),
          color: 'red',
        });
        throw new OfflineError('No internet connection available');
      }
    }

    const timeEnd = window.performance.now();
    const success = response.status >= 200 && response.status < 300;

    if (options.download && success) {
      // const blob = await response.blob();
      // const fileName = (
      //   response.headers.get('content-disposition') || ''
      // ).split('filename=')[1];
      // download(blob, trim(fileName, '"'));
      return undefined as T;
    } else if (success && response.status === 204) {
      return undefined as T;
    } else if (success) {
      return response.json() as Promise<T>;
    }

    // Handle 401, log out user
    if (response.status === 401) {
      // await stores.auth.logout(true, false);
      notifications.show({
        title: i18n.t('error.authorization-error-title', 'Authorization Error'),
        message: i18n.t(
          'error.authorization-error-message',
          'You are not authorized to access this resource'
        ),
        color: 'red',
      });
      throw new AuthorizationError();
    }

    if (response.status === 502) {
      const text = await response.text();
      const err = new BadGatewayError(text);

      Logger.error('BadGatewayError', err, {
        url: urlToFetch,
        requestTime: Math.round(timeEnd - timeStart),
        responseText: text,
        responseHeaders: Object.fromEntries(response.headers.entries()),
      });
      notifications.show({
        title: i18n.t('error.bad-gateway-error-title', 'Bad gateway error'),
        message: i18n.t('error.bad-gateway-error-message', 'Bad gateway error'),
        color: 'red',
      });
      throw err;
    }

    // Handle failed responses
    const error: {
      message?: string;
      error?: string;
      data?: Record<string, unknown>;
    } = {};

    try {
      const parsed = await response.json();
      error.message = parsed.message || '';
      error.error = parsed.error;
      error.data = parsed.data;
    } catch {
      // we're trying to parse an error so JSON may not be valid
    }

    if (response.status === 400 && error.error === 'editor_update_required') {
      window.location.reload();
      throw new UpdateRequiredError(error.message);
    }

    if (response.status === 400) {
      throw new BadRequestError(error.message);
    }

    if (response.status === 402) {
      throw new PaymentRequiredError(error.message);
    }

    if (response.status === 403) {
      // if (error.error === 'user_suspended') {
      //   await stores.auth.logout(false, false);
      // }

      notifications.show({
        title: i18n.t('error.authorization-error-title', 'Authorization Error'),
        message:
          error.message ||
          i18n.t(
            'error.authorization-error-message',
            'You are not authorized to access this resource'
          ),
        color: 'red',
      });
      throw new AuthorizationError(error.message);
    }

    if (response.status === 404) {
      throw new NotFoundError(error.message);
    }

    if (response.status === 503) {
      throw new ServiceUnavailableError(error.message);
    }

    if (response.status === 422) {
      throw new UnprocessableEntityError(error.message);
    }

    if (response.status === 429) {
      throw new RateLimitExceededError('Too many requests, try again in a minute.');
    }

    const err = new RequestError(`Error ${response.status}`);
    Logger.error('Request failed', err, {
      ...error,
      url: urlToFetch,
    });

    // Still need to throw to trigger retry
    throw err;
  };

  get = <T = unknown>(path: string, data: JSONObject | undefined, options?: FetchOptions) =>
    this.fetch<T>(path, 'GET', data, options);

  post = <T = unknown>(
    path: string,
    data?: JSONObject | FormData | undefined,
    options?: FetchOptions
  ) => this.fetch<T>(path, 'POST', data, options);

  put = <T = unknown>(
    path: string,
    data?: JSONObject | FormData | undefined,
    options?: FetchOptions
  ) => this.fetch<T>(path, 'PUT', data, options);

  delete = <T = unknown>(
    path: string,
    data?: JSONObject | FormData | undefined,
    options?: FetchOptions
  ) => this.fetch<T>(path, 'DELETE', data, options);

  patch = <T = unknown>(
    path: string,
    data?: JSONObject | FormData | undefined,
    options?: FetchOptions
  ) => this.fetch<T>(path, 'PATCH', data, options);
}

export const apiClient = new ApiClient();
